import { useMediaQuery } from "@mui/material";
import React, { useId } from "react";
import { theme } from "@/theme";
import { Badge, ImageAttributes, productSelect } from "@/store";
import { ProductBadge } from "./ProductBadge";
import { Card, CardProps } from "@/components/Card";
import { Square } from "@/components/Square";
import { SizedImage } from "@/components/SizedImage";
import { ProductScent } from "./ProductScent";
import { Money } from "@/helpers/Money";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { OneTimeIcon } from "@/components/icons";
import { DroppsProduct } from "@/store";

export interface ProductCardProduct {
  id?: number;
  featuredImage: ImageAttributes;
  featuredText?: string;
  type?: string;
  url?: string;
  title: string;
  size?: string;
  subscription?: string;
  price?: Money;
  compareAtPrice?: Money;
  badge?: Badge;
  product?: DroppsProduct;
  collectionProducts?: DroppsProduct[];
}

export type ProductCardValue = Omit<ProductCardProduct, "featuredImage"> & {
  featuredImage?: Partial<ImageAttributes>;
};

export interface ProductCardProps extends Omit<CardProps, "children"> {
  productCardValue: ProductCardValue;
  productTitle?: React.ReactNode;
  truncateText?: boolean;
  compact?: boolean;
  bordered?: boolean;
  rounded?: boolean;
  hoverable?: any;
  fullwidth?: boolean;
  isSubBadge?: boolean;
  scentBg?: "white" | "gray";
  showRatings?: boolean;
  quickAddButtons?: React.ReactNode;
  hoverImagesOnly?: boolean;
  defaultProductImageTest?: number;
  showFeaturedText?: boolean;
  showProductCardDescription?: boolean;
  isTrialProduct?: boolean;
}

export const ProductCard = (props: ProductCardProps) => {
  const {
    productCardValue,
    productTitle,
    truncateText = false,
    compact,
    cover,
    actions,
    isSubBadge,
    showRatings = false,
    isTrialProduct = false,
    ...cardProps
  } = props;
  const cardContentId = useId();

  const isSmallScreen = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.md)
  );

  let subBadgeUi = undefined;
  if (isSubBadge !== null && isSubBadge !== undefined) {
    subBadgeUi = (
      <div className="!-mt-4 flex justify-end">
        <div className="-mb-4 -mr-4 flex items-center rounded-tl-lg bg-primary-50 px-2 py-2 text-xs text-primary">
          {isSubBadge ? (
            <>
              <AutorenewIcon sx={{ fontSize: "1.5em" }} />
              <span className="pl-1 ">Subscription</span>
            </>
          ) : (
            <>
              <OneTimeIcon sx={{ fontSize: "1.5em" }} />
              <span className="pl-1">One Time</span>
            </>
          )}
        </div>
      </div>
    );
  }

  const scent = productSelect.scent(productCardValue);
  const productCardImage =
    productCardValue?.featuredImage?.src === undefined ||
    productCardValue?.featuredImage?.src === "" ? null : compact ? (
      <div className="relative w-20 shrink-0 [&>img]:max-w-full">
        <SizedImage
          src={productCardValue.featuredImage.src}
          alt={productCardValue.title}
          height={160}
          width={160}
          scaleFactor={2}
          loading="lazy"
        />
        {productCardValue.badge &&
          (productCardValue.badge.text || productCardValue.badge.image) &&
          (productCardValue.badge.text ? (
            <ProductBadge text={productCardValue.badge.text} />
          ) : (
            <ProductBadge
              image={productCardValue.badge.image}
              position={productCardValue.badge.position}
              width={productCardValue.badge.width}
              text={productCardValue.badge.text}
            />
          ))}
      </div>
    ) : (
      <div className="relative">
        <Square>
          <img
            data-id="product-card-image"
            src={productCardValue.featuredImage.src}
            alt={productCardValue.title}
            loading={productCardValue.featuredImage.loading}
            width={productCardValue.featuredImage.width}
            height={productCardValue.featuredImage.height}
          />
        </Square>
        {productCardValue.badge &&
          (productCardValue.badge.text || productCardValue.badge.image) &&
          (productCardValue.badge.text ? (
            <ProductBadge text={productCardValue.badge.text} />
          ) : (
            <ProductBadge
              image={productCardValue.badge.image}
              position={productCardValue.badge.position}
              width={productCardValue.badge.width}
              text={productCardValue.badge.text}
            />
          ))}
      </div>
    );

  const productCardContent = (
    <div
      id={cardContentId}
      className={`relative flex h-full ${
        compact ? "flex-row space-x-4" : "flex-col"
      }`}
    >
      {cover === undefined ? productCardImage : cover || null}
      <div
        data-id="product-card-content"
        className={`flex grow flex-col space-y-3 text-left ${
          compact ? "basis-9/12" : "mt-3"
        }`}
      >
        <div className="space-y-1">
          {showRatings && (
            <div data-id="product-card-ratings">
              <div
                data-bv-show="inline_rating"
                data-bv-product-id={productCardValue.product?.id}
                data-bv-seo="false"
                style={{ minHeight: "22px" }}
              ></div>
            </div>
          )}
          {productCardValue.type && (
            <p
              data-id="product-card-department"
              className={`subtitle text-xs ${!compact ? "sm:text-sm" : ""}`}
            >
              {productCardValue.type}
            </p>
          )}
          {productTitle ?? (
            <h3
              data-id="product-card-title"
              className={`title text-sm ${!compact && "sm:text-base"} ${
                truncateText ? "truncate" : ""
              }`}
            >
              {productSelect.name(productCardValue)}
            </h3>
          )}
          {scent && (
            <ProductScent
              className="product-card-scent-name self-start pl-1 sm:pl-2"
              data-id="product-card-scent-name"
              scentName={scent}
              size={compact ? "sm" : "base"}
            />
          )}
        </div>
        {productCardValue.featuredText && (
          <>
            {!compact && (
              <hr data-id="product-card-divider" className="divider-x my-0" />
            )}
            <p
              data-id="product-card-featured-text"
              className={`body text-sm ${compact ? "" : "sm:text-base"}`}
            >
              {productCardValue.featuredText}
            </p>
          </>
        )}
        {(productCardValue?.size ||
          productCardValue?.subscription ||
          productCardValue?.price) && (
          <div
            className={`product-card-size-price flex items-end justify-between space-x-2`}
          >
            {(productCardValue?.size || productCardValue?.subscription) && (
              <div>
                {productCardValue?.size && (
                  <p
                    data-id="product-card-size"
                    className={`body text-sm ${compact ? "" : "sm:text-base"}`}
                  >
                    {productCardValue.size}
                  </p>
                )}
                {productCardValue?.subscription && !isTrialProduct && (
                  <p
                    data-id="product-card-subscription"
                    className={`body text-sm ${compact ? "" : "sm:text-base"}`}
                  >
                    {productCardValue.subscription}
                  </p>
                )}
                {isTrialProduct && (
                  <p
                    data-id="product-card-subscription"
                    className={`product-card-trial-banner mt-2 bg-blue-100 ${
                      compact ? "" : "sm:text-base"
                    }`}
                  >
                    Full-size products ship <strong>18 days</strong> after
                    purchase and renews every 4 months
                  </p>
                )}
              </div>
            )}
            {productCardValue?.price != undefined ? (
              <div className="flex flex-wrap items-center justify-end space-x-2">
                <p
                  data-id="product-card-price"
                  className={`body text-sm font-semibold ${
                    compact ? "" : "sm:text-lg"
                  } ${
                    productCardValue?.size || productCardValue?.subscription
                      ? "order-2 ml-2"
                      : ""
                  }`}
                >
                  <span className="sr-only">Current price</span>
                  {productCardValue.price.format()}
                </p>
                {productCardValue?.compareAtPrice != undefined ? (
                  <p
                    data-id="product-card-compare-at-price"
                    className={`body text-sm text-gray line-through ${
                      productCardValue?.size || productCardValue?.subscription
                        ? "order-1"
                        : "ml-2"
                    }`}
                  >
                    <span className="sr-only">Regular price</span>
                    {productCardValue.compareAtPrice.format()}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Card
      data-id="product-card"
      {...cardProps}
      actions={actions && <Card.Actions>{actions}</Card.Actions>}
      hoverable={cardProps.hoverable ?? !isSmallScreen}
      bordered={cardProps.bordered ?? false}
    >
      {productCardValue.url ? (
        <a
          href={productCardValue.url}
          aria-label={`Shop ${productCardValue.title}`}
          aria-describedby={cardContentId}
        >
          {productCardContent}
        </a>
      ) : (
        productCardContent
      )}
      {subBadgeUi}
    </Card>
  );
};
